import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getAdminClient } from '../../services/adminService';
import { pricingService } from '../../services/pricingService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Toast from '../../components/ui/Toast';

const AdminPricing = () => {
  const navigate = useNavigate();
  const db = getAdminClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Settings
  const [settings, setSettings] = useState({
    base_hour_price: 200,
    vat_percent: 15,
    is_enabled: true
  });

  // Multipliers
  const [cityMultipliers, setCityMultipliers] = useState([]);
  const [experienceMultipliers, setExperienceMultipliers] = useState([]);
  const [equipmentMultipliers, setEquipmentMultipliers] = useState([]);
  const [typeMultipliers, setTypeMultipliers] = useState([]);
  const [shootingMethodMultipliers, setShootingMethodMultipliers] = useState([]);
  const [hoursMultipliers, setHoursMultipliers] = useState([]);
  const [workloadMultipliers, setWorkloadMultipliers] = useState([]);
  const [placeTypeMultipliers, setPlaceTypeMultipliers] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);

  // Deliverables Settings (Photos/Videos pricing)
  const [deliverablesSettings, setDeliverablesSettings] = useState({
    photoBasePrice: 20,
    videoBasePrice: 100,
    shortVideoBasePrice: 50,
    photoBulkDiscounts: [
      { minCount: 20, discount: 12 },
      { minCount: 50, discount: 15 },
    ],
    videoBulkDiscounts: [
      { minCount: 3, discount: 15 },
      { minCount: 5, discount: 40 },
    ],
    shortVideoBulkDiscounts: [],
  });
  const [savingDeliverables, setSavingDeliverables] = useState(false);

  // Editing states
  const [editingCity, setEditingCity] = useState(null);
  const [editingExperience, setEditingExperience] = useState(null);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editingShootingMethod, setEditingShootingMethod] = useState(null);
  const [editingHours, setEditingHours] = useState(null);
  const [editingWorkload, setEditingWorkload] = useState(null);
  const [editingProductSize, setEditingProductSize] = useState(null);
  const [editingPlaceType, setEditingPlaceType] = useState(null);
  const [editingDiscountCode, setEditingDiscountCode] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      if (!silent) setLoading(true);
      
      // Load settings
      const { data: settingsData } = await db
        .from('pricing_settings')
        .select('*')
        .eq('id', '00000000-0000-0000-0000-000000000001')
        .single();

      if (settingsData) {
        setSettings(settingsData);
      }

      // Load multipliers
      const [cities, experiences, equipment, types, shootingMethods, hours, workload, placeTypes] = await Promise.all([
        db.from('pricing_city_multipliers').select('*').order('city_label'),
        db.from('pricing_experience_multipliers').select('*').order('experience_label'),
        db.from('pricing_equipment_multipliers').select('*').order('equipment_label'),
        db.from('pricing_photography_type_multipliers').select('*').order('type_label'),
        db.from('pricing_shooting_method_multipliers').select('*').order('method_label'),
        db.from('pricing_hours_multipliers').select('*').order('hours'),
        db
          .from('pricing_workload_multipliers')
          .select('*')
          .order('group_key', { ascending: true })
          .order('sort_order', { ascending: true }),
        db.from('pricing_place_type_multipliers').select('*').order('place_label'),
      ]);

      setCityMultipliers(cities.data || []);
      setExperienceMultipliers(experiences.data || []);
      setEquipmentMultipliers(equipment.data || []);
      setTypeMultipliers(types.data || []);
      setShootingMethodMultipliers(shootingMethods.data || []);
      setHoursMultipliers(hours.data || []);
      setWorkloadMultipliers(workload.data || []);
      setPlaceTypeMultipliers(placeTypes.data || []);
      if (workload.error) {
        console.warn('pricing_workload_multipliers:', workload.error.message);
      }

      // Load discount codes (separate query — independent feature from pricing multipliers)
      const { data: discountCodesData, error: discountCodesError } = await db
        .from('discount_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (discountCodesError) {
        console.warn('discount_codes:', discountCodesError.message);
      }
      setDiscountCodes(discountCodesData || []);

      // Debug: Check if shooting methods loaded
      if (shootingMethods.error) {
        console.error('Error loading shooting method multipliers:', shootingMethods.error);
      } else {
        console.log('Shooting method multipliers loaded:', shootingMethods.data?.length || 0);
      }

      // Load deliverables settings
      const { data: deliverablesData } = await db
        .from('app_settings')
        .select('*')
        .eq('key', 'deliverables_settings');

      if (deliverablesData && deliverablesData.length > 0 && deliverablesData[0]?.value) {
        try {
          const parsedSettings = JSON.parse(deliverablesData[0].value);
          setDeliverablesSettings(parsedSettings);
          console.log('Deliverables settings loaded:', parsedSettings);
        } catch (e) {
          console.log('Failed to parse deliverables settings, using defaults');
        }
      }
    } catch (error) {
      console.error('Failed to load pricing data:', error);
      setToast({ type: 'error', message: 'فشل تحميل بيانات التسعير' });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Save Deliverables Settings
  const saveDeliverablesSettings = async () => {
    setSavingDeliverables(true);
    try {
      const { data: existing } = await db
        .from('app_settings')
        .select('id')
        .eq('key', 'deliverables_settings');

      const settingsValue = JSON.stringify(deliverablesSettings);

      if (existing && existing.length > 0) {
        const { error } = await db
          .from('app_settings')
          .update({ value: settingsValue, updated_at: new Date().toISOString() })
          .eq('key', 'deliverables_settings');
        if (error) throw error;
      } else {
        const { error } = await db
          .from('app_settings')
          .insert({ 
            key: 'deliverables_settings', 
            value: settingsValue,
            description: 'إعدادات تسعير المخرجات - معامل الصور والفيديوهات',
            category: 'pricing'
          });
        if (error) throw error;
      }

      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ إعدادات تسعير المخرجات بنجاح' });
    } catch (error) {
      console.error('Error saving deliverables settings:', error);
      setToast({ type: 'error', message: 'فشل حفظ الإعدادات' });
    } finally {
      setSavingDeliverables(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const { error } = await db
        .from('pricing_settings')
        .upsert({
          id: '00000000-0000-0000-0000-000000000001',
          ...settings,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ الإعدادات بنجاح' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setToast({ type: 'error', message: 'فشل حفظ الإعدادات' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCity = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_city_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_city_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingCity(null);
      loadData();
    } catch (error) {
      console.error('Failed to save city multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExperience = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_experience_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_experience_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingExperience(null);
      loadData();
    } catch (error) {
      console.error('Failed to save experience multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEquipment = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_equipment_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_equipment_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingEquipment(null);
      loadData();
    } catch (error) {
      console.error('Failed to save equipment multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveType = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_photography_type_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_photography_type_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingType(null);
      loadData();
    } catch (error) {
      console.error('Failed to save type multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShootingMethod = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_shooting_method_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_shooting_method_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingShootingMethod(null);
      loadData();
    } catch (error) {
      console.error('Failed to save shooting method multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_hours_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_hours_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingHours(null);
      loadData();
    } catch (error) {
      console.error('Failed to save hours multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (table, id, currentStatus) => {
    try {
      setSaving(true);
      const { error } = await db
        .from(table)
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      pricingService.clearPricingCache();
      loadData();
    } catch (error) {
      console.error('Failed to toggle active:', error);
      setToast({ type: 'error', message: 'فشل تحديث الحالة' });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlaceType = async (data) => {
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_place_type_multipliers')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_place_type_multipliers')
          .insert(data);
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingPlaceType(null);
      loadData();
    } catch (error) {
      console.error('Failed to save place type multiplier:', error);
      setToast({ type: 'error', message: 'فشل حفظ المضاعف' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWorkloadRow = async (data) => {
    const mult = parseFloat(String(data.multiplier));
    if (Number.isNaN(mult) || mult <= 0 || mult > 99) {
      setToast({ type: 'error', message: 'المضاعف يجب أن يكون رقماً بين 0.01 و 99' });
      return;
    }
    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('pricing_workload_multipliers')
          .update({
            label_ar: data.label_ar,
            description_ar: data.description_ar || null,
            multiplier: mult,
            is_active: data.is_active !== false,
            updated_at: new Date().toISOString(),
          })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from('pricing_workload_multipliers')
          .insert({
            multiplier_kind: data.multiplier_kind,
            bucket_key: data.bucket_key,
            group_key: data.group_key || 'custom',
            group_label_ar: data.group_label_ar || 'مخصص',
            label_ar: data.label_ar,
            description_ar: data.description_ar || null,
            multiplier: mult,
            is_active: data.is_active !== false,
            is_system: false,
          });
        if (error) throw error;
      }
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم حفظ المضاعف بنجاح' });
      setEditingWorkload(null);
      setEditingProductSize(null);
      loadData();
    } catch (error) {
      console.error('Failed to save workload multiplier:', error);
      setToast({ type: 'error', message: error.message || 'فشل الحفظ' });
    } finally {
      setSaving(false);
    }
  };

  // ── أكواد الخصم ──────────────────────────────────────────────────────────
  const handleSaveDiscountCode = async (data) => {
    const code = String(data.code || '').trim().toUpperCase();
    const percent = parseFloat(data.discount_percent);

    if (!/^[A-Z0-9_-]{3,30}$/.test(code)) {
      setToast({ type: 'error', message: 'كود الخصم يجب أن يتكون من 3 إلى 30 حرفاً/رقماً إنجليزياً (يمكن استخدام - أو _)' });
      return;
    }
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      setToast({ type: 'error', message: 'نسبة الخصم يجب أن تكون رقماً بين 1 و 100' });
      return;
    }
    if (!data.starts_at || !data.expires_at) {
      setToast({ type: 'error', message: 'يرجى تحديد تاريخ البدء وتاريخ الانتهاء' });
      return;
    }

    const startsAtIso = new Date(`${data.starts_at}T00:00:00`).toISOString();
    const expiresAtIso = new Date(`${data.expires_at}T23:59:59`).toISOString();
    if (new Date(expiresAtIso) <= new Date(startsAtIso)) {
      setToast({ type: 'error', message: 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البدء' });
      return;
    }

    const maxUsesParsed = data.max_uses !== '' && data.max_uses !== null && data.max_uses !== undefined
      ? parseInt(data.max_uses, 10)
      : null;

    const payload = {
      code,
      discount_percent: percent,
      starts_at: startsAtIso,
      expires_at: expiresAtIso,
      max_uses: Number.isFinite(maxUsesParsed) && maxUsesParsed > 0 ? maxUsesParsed : null,
      description: data.description?.trim() || null,
      is_active: data.is_active !== false,
    };

    try {
      setSaving(true);
      if (data.id) {
        const { error } = await db
          .from('discount_codes')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await db.from('discount_codes').insert(payload);
        if (error) throw error;
      }
      setToast({ type: 'success', message: 'تم حفظ كود الخصم بنجاح' });
      setEditingDiscountCode(null);
      loadData();
    } catch (error) {
      console.error('Failed to save discount code:', error);
      const msg = error?.code === '23505'
        ? 'هذا الكود مستخدم مسبقاً، اختر كوداً آخر'
        : (error.message || 'فشل حفظ كود الخصم');
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDiscountCodeActive = async (id, currentStatus) => {
    try {
      setSaving(true);
      const { error } = await db
        .from('discount_codes')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      loadData();
    } catch (error) {
      console.error('Failed to toggle discount code:', error);
      setToast({ type: 'error', message: 'فشل تحديث الحالة' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDiscountCode = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف كود الخصم هذا؟')) return;
    try {
      setSaving(true);
      const { error } = await db.from('discount_codes').delete().eq('id', id);
      if (error) throw error;
      setToast({ type: 'success', message: 'تم الحذف بنجاح' });
      loadData();
    } catch (error) {
      console.error('Failed to delete discount code:', error);
      setToast({ type: 'error', message: 'فشل الحذف' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (table, id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المضاعف؟')) return;
    try {
      setSaving(true);
      const { error } = await db
        .from(table)
        .delete()
        .eq('id', id);
      if (error) throw error;
      pricingService.clearPricingCache();
      setToast({ type: 'success', message: 'تم الحذف بنجاح' });
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      setToast({ type: 'error', message: 'فشل الحذف' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">إدارة التسعير الذكي</h1>
            <p className="text-slate-600">التحكم الكامل في نظام التسعير الديناميكي</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline" iconName="ArrowRight">
            العودة للوحة التحكم
          </Button>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">الإعدادات الأساسية</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Input
              label="السعر الأساسي للساعة (ريال)"
              type="number"
              value={settings.base_hour_price}
              onChange={(e) => setSettings({ ...settings, base_hour_price: parseFloat(e.target.value) || 0 })}
            />
            <Input
              label="نسبة ضريبة القيمة المضافة (%)"
              type="number"
              value={settings.vat_percent}
              onChange={(e) => setSettings({ ...settings, vat_percent: parseFloat(e.target.value) || 0 })}
            />
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                checked={settings.is_enabled}
                onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <label className="text-sm font-medium">تفعيل التسعير الذكي</label>
            </div>
          </div>
          <Button onClick={handleSaveSettings} loading={saving} iconName="Save">
            حفظ الإعدادات
          </Button>
        </div>

        {/* Discount Codes */}
        <DiscountCodesSection
          data={discountCodes}
          editing={editingDiscountCode}
          onEdit={setEditingDiscountCode}
          onClose={() => setEditingDiscountCode(null)}
          onSave={handleSaveDiscountCode}
          onToggleActive={handleToggleDiscountCodeActive}
          onDelete={handleDeleteDiscountCode}
          saving={saving}
        />

        {/* Deliverables Pricing Settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white">
                <Icon name="Images" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">تسعير المخرجات (الصور والفيديوهات)</h2>
                <p className="text-sm text-slate-500">تحديد سعر كل صورة وفيديو وخصومات الكميات</p>
              </div>
            </div>
            <Button 
              onClick={saveDeliverablesSettings} 
              loading={savingDeliverables}
              iconName="Save"
            >
              حفظ الإعدادات
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Photo Pricing */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Image" size={20} className="text-blue-600" />
                <h3 className="font-bold text-blue-800">تسعير الصور</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-blue-700 block mb-1">سعر الصورة الواحدة (ريال)</label>
                  <input
                    type="number"
                    value={deliverablesSettings.photoBasePrice}
                    onChange={(e) => setDeliverablesSettings(prev => ({
                      ...prev,
                      photoBasePrice: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    min="0"
                    step="0.5"
                  />
                </div>

                <div>
                  <label className="text-sm text-blue-700 block mb-2">خصومات الكميات</label>
                  <div className="space-y-2">
                    {deliverablesSettings.photoBulkDiscounts?.map((discount, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-blue-600">عند</span>
                        <input
                          type="number"
                          value={discount.minCount}
                          onChange={(e) => {
                            const newDiscounts = [...deliverablesSettings.photoBulkDiscounts];
                            newDiscounts[idx].minCount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, photoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-blue-200 text-center"
                          min="1"
                        />
                        <span className="text-blue-600">صورة+:</span>
                        <input
                          type="number"
                          value={discount.discount}
                          onChange={(e) => {
                            const newDiscounts = [...deliverablesSettings.photoBulkDiscounts];
                            newDiscounts[idx].discount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, photoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-blue-200 text-center"
                          min="0"
                          max="100"
                        />
                        <span className="text-blue-600">% خصم</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Long Video Pricing */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Video" size={20} className="text-blue-600" />
                <h3 className="font-bold text-blue-800">تسعير الفيديو الطويل</h3>
                <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">فيديو احترافي / طويل</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-blue-800 block mb-1">سعر الفيديو الطويل الواحد (ريال)</label>
                  <input
                    type="number"
                    value={deliverablesSettings.videoBasePrice}
                    onChange={(e) => setDeliverablesSettings(prev => ({
                      ...prev,
                      videoBasePrice: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    min="0"
                    step="5"
                  />
                </div>

                <div>
                  <label className="text-sm text-blue-800 block mb-2">خصومات الكميات</label>
                  <div className="space-y-2">
                    {deliverablesSettings.videoBulkDiscounts?.map((discount, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-blue-600">عند</span>
                        <input
                          type="number"
                          value={discount.minCount}
                          onChange={(e) => {
                            const newDiscounts = [...deliverablesSettings.videoBulkDiscounts];
                            newDiscounts[idx].minCount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, videoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-blue-200 text-center"
                          min="1"
                        />
                        <span className="text-blue-600">فيديو+:</span>
                        <input
                          type="number"
                          value={discount.discount}
                          onChange={(e) => {
                            const newDiscounts = [...deliverablesSettings.videoBulkDiscounts];
                            newDiscounts[idx].discount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, videoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-blue-200 text-center"
                          min="0"
                          max="100"
                        />
                        <span className="text-blue-600">% خصم</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Short Video Pricing */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-5 border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Clapperboard" size={20} className="text-purple-600" />
                <h3 className="font-bold text-purple-800">تسعير الفيديو القصير</h3>
                <span className="text-xs text-purple-500 bg-purple-100 px-2 py-0.5 rounded-full">مناسب للسوشيال ميديا</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-purple-800 block mb-1">سعر الفيديو القصير الواحد (ريال)</label>
                  <input
                    type="number"
                    value={deliverablesSettings.shortVideoBasePrice ?? 50}
                    onChange={(e) => setDeliverablesSettings(prev => ({
                      ...prev,
                      shortVideoBasePrice: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 rounded-lg border border-purple-200 bg-white focus:ring-2 focus:ring-purple-400 focus:outline-none"
                    min="0"
                    step="5"
                  />
                </div>

                <div>
                  <label className="text-sm text-purple-800 block mb-2">خصومات الكميات</label>
                  <div className="space-y-2">
                    {(deliverablesSettings.shortVideoBulkDiscounts || []).map((discount, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-purple-600">عند</span>
                        <input
                          type="number"
                          value={discount.minCount}
                          onChange={(e) => {
                            const newDiscounts = [...(deliverablesSettings.shortVideoBulkDiscounts || [])];
                            newDiscounts[idx].minCount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, shortVideoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-purple-200 text-center"
                          min="1"
                        />
                        <span className="text-purple-600">فيديو+:</span>
                        <input
                          type="number"
                          value={discount.discount}
                          onChange={(e) => {
                            const newDiscounts = [...(deliverablesSettings.shortVideoBulkDiscounts || [])];
                            newDiscounts[idx].discount = parseInt(e.target.value) || 0;
                            setDeliverablesSettings(prev => ({ ...prev, shortVideoBulkDiscounts: newDiscounts }));
                          }}
                          className="w-16 px-2 py-1 rounded border border-purple-200 text-center"
                          min="0"
                          max="100"
                        />
                        <span className="text-purple-600">% خصم</span>
                      </div>
                    ))}
                    {(deliverablesSettings.shortVideoBulkDiscounts || []).length === 0 && (
                      <p className="text-xs text-purple-400">لا توجد خصومات كميات. يمكن إضافتها لاحقاً.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Calculator */}
          <div className="mt-6 bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <Icon name="Calculator" size={18} />
              معاينة السعر
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="text-2xl font-bold text-blue-600">{deliverablesSettings.photoBasePrice} ر.س</div>
                <div className="text-xs text-slate-500">للصورة الواحدة</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="text-2xl font-bold text-blue-600">{deliverablesSettings.videoBasePrice} ر.س</div>
                <div className="text-xs text-slate-500">فيديو طويل</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-purple-600">{deliverablesSettings.shortVideoBasePrice ?? 50} ر.س</div>
                <div className="text-xs text-slate-500">فيديو قصير</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="text-2xl font-bold text-blue-600">
                  {(deliverablesSettings.photoBasePrice * 20 * (1 - (deliverablesSettings.photoBulkDiscounts?.[0]?.discount || 0) / 100)).toFixed(0)} ر.س
                </div>
                <div className="text-xs text-slate-500">20 صورة (مع الخصم)</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="text-2xl font-bold text-blue-600">
                  {(deliverablesSettings.videoBasePrice * 3 * (1 - (deliverablesSettings.videoBulkDiscounts?.[0]?.discount || 0) / 100)).toFixed(0)} ر.س
                </div>
                <div className="text-xs text-slate-500">3 فيديو طويل (مع الخصم)</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="text-2xl font-bold text-purple-600">
                  {((deliverablesSettings.shortVideoBasePrice ?? 50) * 3).toFixed(0)} ر.س
                </div>
                <div className="text-xs text-slate-500">3 فيديو قصير</div>
              </div>
            </div>
          </div>
        </div>

        {/* Workload Multipliers */}
        <MultiplierTable
          title="مضاعفات حجم العمل"
          data={workloadMultipliers.filter(r => r.multiplier_kind === 'workload')}
          columns={[
            { key: 'label_ar', label: 'التسمية' },
            { key: 'bucket_key', label: 'المفتاح' },
            { key: 'group_label_ar', label: 'المجموعة' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingWorkload}
          onToggleActive={(id, status) => handleToggleActive('pricing_workload_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_workload_multipliers', id)}
          editing={editingWorkload}
          onSave={handleSaveWorkloadRow}
          onClose={() => setEditingWorkload(null)}
          saving={saving}
          defaultValues={{ multiplier_kind: 'workload', group_key: 'custom', group_label_ar: 'مخصص' }}
          fields={[
            { key: 'bucket_key', label: 'المفتاح (مثال: small)', required: true },
            { key: 'label_ar', label: 'التسمية بالعربية', required: true },
            { key: 'description_ar', label: 'الوصف (اختياري)' },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true },
          ]}
        />

        {/* Product Physical Size Multipliers */}
        <MultiplierTable
          title="مضاعفات الحجم الفعلي للمنتجات"
          data={workloadMultipliers.filter(r => r.multiplier_kind === 'product_physical')}
          columns={[
            { key: 'label_ar', label: 'التسمية' },
            { key: 'bucket_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingProductSize}
          onToggleActive={(id, status) => handleToggleActive('pricing_workload_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_workload_multipliers', id)}
          editing={editingProductSize}
          onSave={handleSaveWorkloadRow}
          onClose={() => setEditingProductSize(null)}
          saving={saving}
          defaultValues={{ multiplier_kind: 'product_physical', group_key: 'product_size', group_label_ar: 'تصوير المنتجات — الحجم الفعلي' }}
          fields={[
            { key: 'bucket_key', label: 'المفتاح (مثال: compact)', required: true },
            { key: 'label_ar', label: 'التسمية بالعربية', required: true },
            { key: 'description_ar', label: 'الوصف (اختياري)' },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true },
          ]}
        />

        {/* City Multipliers */}
        <MultiplierTable
          title="مضاعفات المدن"
          data={cityMultipliers}
          columns={[
            { key: 'city_label', label: 'المدينة' },
            { key: 'city_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingCity}
          onToggleActive={(id, status) => handleToggleActive('pricing_city_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_city_multipliers', id)}
          editing={editingCity}
          onSave={handleSaveCity}
          onClose={() => setEditingCity(null)}
          saving={saving}
          fields={[
            { key: 'city_key', label: 'المفتاح (مثال: riyadh)', required: true },
            { key: 'city_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Experience Multipliers */}
        <MultiplierTable
          title="مضاعفات مستوى الخبرة"
          data={experienceMultipliers}
          columns={[
            { key: 'experience_label', label: 'المستوى' },
            { key: 'experience_level_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingExperience}
          onToggleActive={(id, status) => handleToggleActive('pricing_experience_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_experience_multipliers', id)}
          editing={editingExperience}
          onSave={handleSaveExperience}
          onClose={() => setEditingExperience(null)}
          saving={saving}
          fields={[
            { key: 'experience_level_key', label: 'المفتاح (مثال: certified)', required: true },
            { key: 'experience_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Equipment Multipliers */}
        <MultiplierTable
          title="مضاعفات المعدات"
          data={equipmentMultipliers}
          columns={[
            { key: 'equipment_label', label: 'المعدة' },
            { key: 'equipment_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingEquipment}
          onToggleActive={(id, status) => handleToggleActive('pricing_equipment_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_equipment_multipliers', id)}
          editing={editingEquipment}
          onSave={handleSaveEquipment}
          onClose={() => setEditingEquipment(null)}
          saving={saving}
          fields={[
            { key: 'equipment_key', label: 'المفتاح (مثال: drone)', required: true },
            { key: 'equipment_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Photography Type Multipliers */}
        <MultiplierTable
          title="مضاعفات نوع التصوير"
          data={typeMultipliers}
          columns={[
            { key: 'type_label', label: 'النوع' },
            { key: 'type_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingType}
          onToggleActive={(id, status) => handleToggleActive('pricing_photography_type_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_photography_type_multipliers', id)}
          editing={editingType}
          onSave={handleSaveType}
          onClose={() => setEditingType(null)}
          saving={saving}
          fields={[
            { key: 'type_key', label: 'المفتاح (مثال: portrait)', required: true },
            { key: 'type_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Shooting Method Multipliers */}
        <MultiplierTable
          title="مضاعفات وسيلة التصوير"
          data={shootingMethodMultipliers}
          columns={[
            { key: 'method_label', label: 'الوسيلة' },
            { key: 'method_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingShootingMethod}
          onToggleActive={(id, status) => handleToggleActive('pricing_shooting_method_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_shooting_method_multipliers', id)}
          editing={editingShootingMethod}
          onSave={handleSaveShootingMethod}
          onClose={() => setEditingShootingMethod(null)}
          saving={saving}
          fields={[
            { key: 'method_key', label: 'المفتاح (مثال: professional_camera)', required: true },
            { key: 'method_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Place Type Multipliers */}
        <MultiplierTable
          title="مضاعفات مكان التصوير"
          data={placeTypeMultipliers}
          columns={[
            { key: 'place_label', label: 'المكان' },
            { key: 'place_key', label: 'المفتاح' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingPlaceType}
          onToggleActive={(id, status) => handleToggleActive('pricing_place_type_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_place_type_multipliers', id)}
          editing={editingPlaceType}
          onSave={handleSavePlaceType}
          onClose={() => setEditingPlaceType(null)}
          saving={saving}
          fields={[
            { key: 'place_key', label: 'المفتاح (مثال: indoor)', required: true },
            { key: 'place_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {/* Hours Multipliers */}
        <MultiplierTable
          title="مضاعفات عدد الساعات"
          data={hoursMultipliers}
          columns={[
            { key: 'hours_label', label: 'عدد الساعات' },
            { key: 'hours', label: 'الساعات' },
            { key: 'multiplier', label: 'المضاعف', render: (row) => `×${parseFloat(row.multiplier).toFixed(2)}` }
          ]}
          onEdit={setEditingHours}
          onToggleActive={(id, status) => handleToggleActive('pricing_hours_multipliers', id, status)}
          onDelete={(id) => handleDelete('pricing_hours_multipliers', id)}
          editing={editingHours}
          onSave={handleSaveHours}
          onClose={() => setEditingHours(null)}
          saving={saving}
          fields={[
            { key: 'hours', label: 'عدد الساعات', type: 'number', required: true },
            { key: 'hours_label', label: 'الاسم بالعربية', required: true },
            { key: 'multiplier', label: 'المضاعف', type: 'number', step: '0.01', required: true }
          ]}
        />

        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Discount Codes Section ─────────────────────────────────────────────────
const formatDateForInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const formatDateArabic = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const DiscountCodesSection = ({ data, editing, onEdit, onClose, onSave, onToggleActive, onDelete, saving }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (editing) {
      setFormData({
        ...editing,
        starts_at: formatDateForInput(editing.starts_at) || new Date().toISOString().slice(0, 10),
        expires_at: formatDateForInput(editing.expires_at),
        max_uses: editing.max_uses ?? '',
      });
    } else {
      setFormData({});
    }
  }, [editing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const now = new Date();

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
            <Icon name="Tag" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">أكواد الخصم</h2>
            <p className="text-sm text-slate-500">إنشاء وإدارة أكواد خصم بنسبة مئوية تظهر في صفحة الدفع</p>
          </div>
        </div>
        <Button onClick={() => onEdit({ is_active: true })} variant="outline" size="sm" iconName="Plus">
          إضافة كود جديد
        </Button>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Icon name="Tag" size={48} className="mx-auto mb-2 opacity-50" />
          <p>لا توجد أكواد خصم بعد</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الكود</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">النسبة</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">البدء</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الانتهاء</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الاستخدام</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الحالة</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((row) => {
                const expired = new Date(row.expires_at) <= now;
                const exhausted = row.max_uses !== null && row.max_uses !== undefined && row.used_count >= row.max_uses;
                return (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900" dir="ltr">{row.code}</td>
                    <td className="px-4 py-3 text-slate-800">{parseFloat(row.discount_percent)}%</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateArabic(row.starts_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateArabic(row.expires_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{row.used_count} / {row.max_uses ?? '∞'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                          row.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {row.is_active ? 'نشط' : 'معطل'}
                        </span>
                        {expired && <span className="text-xs text-amber-600">منتهي الصلاحية</span>}
                        {exhausted && <span className="text-xs text-amber-600">مستنفَد</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(row)}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          تعديل
                        </button>
                        <button
                          onClick={() => onToggleActive(row.id, row.is_active)}
                          className={`px-2 py-1 text-xs rounded ${
                            row.is_active ? 'bg-amber-600' : 'bg-green-600'
                          } text-white hover:opacity-80`}
                        >
                          {row.is_active ? 'تعطيل' : 'تفعيل'}
                        </button>
                        <button
                          onClick={() => onDelete(row.id)}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} dir="rtl">
            <h3 className="text-lg font-bold mb-4">{editing.id ? 'تعديل كود الخصم' : 'إضافة كود خصم جديد'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  الكود <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary font-mono uppercase"
                  dir="ltr"
                  placeholder="مثال: EID25"
                  maxLength={30}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">أحرف إنجليزية و/أو أرقام فقط (3-30 حرفاً، يمكن استخدام - أو _)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  نسبة الخصم (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={formData.discount_percent ?? ''}
                  onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    تاريخ البدء <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.starts_at || ''}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    تاريخ الانتهاء <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.expires_at || ''}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">الحد الأقصى لعدد مرات الاستخدام (اختياري)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_uses ?? ''}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="اتركه فارغاً لعدد غير محدود"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ملاحظة داخلية (اختياري)</label>
                <input
                  type="text"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="مثال: كود العيد 2026"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm">نشط</label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" onClick={onClose} variant="outline" className="flex-1">
                  إلغاء
                </Button>
                <Button type="submit" loading={saving} className="flex-1">
                  حفظ
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Multiplier Table Component
const MultiplierTable = ({ title, data, columns, onEdit, onToggleActive, onDelete, editing, onSave, onClose, saving, fields, defaultValues = {} }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (editing) {
      setFormData({ ...editing });
    } else {
      setFormData({ is_active: true, ...defaultValues });
    }
  }, [editing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        <Button
          onClick={() => onEdit({ is_active: true, ...defaultValues })}
          variant="outline"
          size="sm"
          iconName="Plus"
        >
          إضافة جديد
        </Button>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Icon name="Package" size={48} className="mx-auto mb-2 opacity-50" />
          <p>لا توجد مضاعفات</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {columns.map(col => (
                  <th key={col.key} className="px-4 py-3 text-right font-semibold text-slate-700">
                    {col.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الحالة</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(row => (
                <tr key={row.id} className="hover:bg-slate-50">
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-slate-800">
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      row.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {row.is_active ? 'نشط' : 'معطل'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit(row)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        تعديل
                      </button>
                      <button
                        onClick={() => onToggleActive(row.id, row.is_active)}
                        className={`px-2 py-1 text-xs rounded ${
                          row.is_active ? 'bg-amber-600' : 'bg-green-600'
                        } text-white hover:opacity-80`}
                      >
                        {row.is_active ? 'تعطيل' : 'تفعيل'}
                      </button>
                      <button
                        onClick={() => onDelete(row.id)}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} dir="rtl">
            <h3 className="text-lg font-bold mb-4">{editing.id ? 'تعديل المضاعف' : 'إضافة مضاعف جديد'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {fields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type={field.type || 'text'}
                    step={field.step}
                    value={formData[field.key] !== undefined ? formData[field.key] : ''}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (field.type === 'number') {
                        // Keep as string for input, but ensure it's a valid number
                        value = e.target.value;
                        // Validate on blur or submit
                      }
                      setFormData({ ...formData, [field.key]: value });
                    }}
                    onBlur={(e) => {
                      if (field.type === 'number' && e.target.value !== '') {
                        const numValue = parseFloat(e.target.value);
                        if (!isNaN(numValue)) {
                          setFormData({ ...formData, [field.key]: numValue });
                        }
                      }
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary"
                    required={field.required}
                  />
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm">نشط</label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" onClick={onClose} variant="outline" className="flex-1">
                  إلغاء
                </Button>
                <Button type="submit" loading={saving} className="flex-1">
                  حفظ
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPricing;
